import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Select, TextField, MenuItem, Button, Chip, Tooltip, Grid, Stack, CircularProgress,
} from '@mui/material';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import http from '../../../Api/http';
import {
    getPayrollCycle, postApprovePayrollCycle, postMarkCreditedPayrollCycle, postRollbackPayrollCycle,
} from '../../../Api/Api';
import PayrollCycleStepper from './PayrollCycleStepper';
import { SkeletonRows, field } from '../../uiKit';
import { useCrumbActionsSetter } from '../../../utils/crumbActions';
import SnackBar from '../../SnackBar';

const PRIMARY = '#135F93';
const PRIMARY_LIGHT = '#E4F0F9';
const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: `0 2px 6px ${PRIMARY}40`, textTransform: 'none', '&:hover': { bgcolor: '#0E4A75' } };

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Keys map to the cycle state the API reports; the wording matches the Register
// tab so the same four steps aren't named two ways on one screen.
const STAGES = [
    { key: 'attendanceLocked', label: 'Attendance Cutoff', desc: 'Lock monthly attendance', icon: EventAvailableRoundedIcon },
    { key: 'calculated', label: 'Salary Calculation', desc: 'Compute gross / LOP / net', icon: CalculateRoundedIcon },
    { key: 'approved', label: 'Manager Approval', desc: 'Review & approve register', icon: HowToRegRoundedIcon },
    { key: 'credited', label: 'Salary Credited', desc: 'Paid to bank & payslips shared', icon: PaidRoundedIcon },
];

// This screen's stage list predates the shared stepper, which takes `description`.
const STEPPER_STAGES = STAGES.map(({ key, label, desc, icon }) => ({ key, label, description: desc, icon }));

const buildMonths = () => {
    const opts = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        opts.push({
            value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
            label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
            isCurrent: i === 0,
        });
    }
    return opts;
};

const prettyStatus = (s) => (s ? String(s).replace(/([a-z])([A-Z])/g, '$1 $2') : '—');

export default function MarkSalaryCreditedPage() {
    const months = useMemo(buildMonths, []);
    const [month, setMonth] = useState(months[0].value);
    const [cycle, setCycle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [snack, setSnack] = useState({ open: false, ok: true, msg: '' });
    const showSnack = (msg, ok = true) => setSnack({ open: true, ok, msg });

    const fetchCycle = useCallback(async () => {
        setLoading(true);
        try {
            const res = await http.get(getPayrollCycle, { params: { payoutMonth: month } });
            if (res.data && !res.data.error) {
                setCycle(res.data.data || null);
            } else {
                setCycle(null);
                showSnack(res.data?.message || 'Failed to load the payroll cycle', false);
            }
        } catch {
            setCycle(null);
            showSnack('Failed to load the payroll cycle', false);
        } finally {
            setLoading(false);
        }
    }, [month]);

    useEffect(() => { fetchCycle(); }, [fetchCycle]);

    const stageState = (key) => cycle?.[key] || { done: false, on: null, by: null };
    const activeIdx = (() => {
        const idx = STAGES.findIndex((s) => !stageState(s.key).done);
        return idx === -1 ? STAGES.length : idx;
    })();

    const payoutMonth = cycle?.payoutMonthRaw || month;
    const runAction = async (url, successMsg) => {
        setActionLoading(true);
        try {
            const res = await http.post(url, { payoutMonth });
            if (res.data && !res.data.error) {
                showSnack(successMsg, true);
                await fetchCycle();
            } else {
                showSnack(res.data?.message || 'Action failed. Please try again.', false);
            }
        } catch {
            showSnack('Action failed. Please try again.', false);
        } finally {
            setActionLoading(false);
        }
    };

    const monthLabel = months.find((m) => m.value === month)?.label || cycle?.payoutMonth || month;

    // The payout month picker sits at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#6B7280' }}>Payout month</Typography>
                <TextField
                    select size="small" value={month} onChange={(e) => setMonth(e.target.value)}
                    sx={{ ...field, width: 210, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 42, bgcolor: '#fff' } }}
                >
                    {months.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}{m.isCurrent ? '  (current)' : ''}</MenuItem>)}
                </TextField>
            </Box>,
        );
        return () => setCrumbActions(null);
    }, [setCrumbActions, month, months]);

    const totals = [
        { label: 'Total Employees', value: cycle?.totalEmployees, fmt: (v) => v, color: '#135F93', bg: '#E4F0F9', icon: GroupsRoundedIcon },
        { label: 'Total Gross', value: cycle?.totalGross, fmt: formatINR, color: '#0EA5E9', bg: '#E0F2FE', icon: ReceiptLongRoundedIcon },
        { label: 'Total Deductions', value: cycle?.totalDeductions, fmt: formatINR, color: '#E11D48', bg: '#FEE2E2', icon: EventBusyRoundedIcon },
        { label: 'Net Payout', value: cycle?.totalNet, fmt: formatINR, color: '#16A34A', bg: '#DCFCE7', icon: PaidRoundedIcon },
    ];

    const renderAction = () => {
        if (loading || !cycle) return null;
        const attendanceDone = stageState('attendanceLocked').done;
        const approvedDone = stageState('approved').done;
        const creditedDone = stageState('credited').done;

        const revertBtn = (approvedDone && !creditedDone) ? (
            <Tooltip arrow title="Revert approval — moves this cycle back to Calculated">
                <Button
                    onClick={() => runAction(postRollbackPayrollCycle, 'Approval reverted — cycle is back to Calculated.')}
                    disabled={actionLoading}
                    startIcon={<ReplayRoundedIcon sx={{ fontSize: 16 }} />}
                    sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 700, color: '#64748B', bgcolor: '#F1F5F9', borderRadius: '7px', height: 40, px: 2, '&:hover': { bgcolor: '#E2E8F0' } }}
                >
                    Revert to Calculated
                </Button>
            </Tooltip>
        ) : null;

        let primary;
        if (!attendanceDone) {
            primary = <Chip icon={<EventBusyRoundedIcon sx={{ fontSize: '15px !important' }} />} label="Lock attendance for this month first" sx={{ height: 38, borderRadius: '7px', fontWeight: 700, fontSize: 12.5, bgcolor: '#FEF3C7', color: '#B45309', '& .MuiChip-icon': { color: '#B45309' } }} />;
        } else if (!approvedDone) {
            primary = <Button onClick={() => runAction(postApprovePayrollCycle, 'Payroll approved — you can now credit salaries.')} disabled={actionLoading} startIcon={actionLoading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <HowToRegRoundedIcon sx={{ fontSize: 18 }} />} sx={{ ...solidBtn, height: 40, px: 2.4 }}>Approve Payroll</Button>;
        } else if (!creditedDone) {
            primary = <Button onClick={() => runAction(postMarkCreditedPayrollCycle, 'Salaries marked as credited.')} disabled={actionLoading} startIcon={actionLoading ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <PaidRoundedIcon sx={{ fontSize: 18 }} />} sx={{ height: 40, px: 2.2, textTransform: 'none', fontSize: 12.5, fontWeight: 700, color: '#fff', bgcolor: '#16A34A', borderRadius: '7px', boxShadow: '0 2px 6px #16A34A40', '&:hover': { bgcolor: '#15803D' } }}>Mark as Credited</Button>;
        } else {
            primary = <Chip icon={<CheckCircleRoundedIcon sx={{ fontSize: '15px !important' }} />} label="Payroll credited — cycle complete" sx={{ height: 38, borderRadius: '7px', fontWeight: 700, fontSize: 12.5, bgcolor: '#DCFCE7', color: '#16A34A', '& .MuiChip-icon': { color: '#16A34A' } }} />;
        }

        return (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                {revertBtn}
                {primary}
            </Stack>
        );
    };

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            <SnackBar open={snack.open} color={snack.ok} setOpen={(v) => setSnack((s) => ({ ...s, open: v }))} status={snack.ok} message={snack.msg} />

            {!setCrumbActions && (
            <Box sx={{ pb: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, flexWrap: 'wrap', gap: 1.5 }}>
                <Box>
                    <Typography sx={{ fontSize: 19, fontWeight: 800, color: '#111827', letterSpacing: '-0.3px' }}>Run Payroll</Typography>
                    <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 0.2 }}>Review calculated salaries, approve and credit them to bank accounts</Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#6B7280' }}>Payout month</Typography>
                    <Select size="small" value={month} onChange={(e) => setMonth(e.target.value)}
                        sx={{ fontSize: 12.5, fontWeight: 600, height: 38, bgcolor: '#fff', borderRadius: '7px', minWidth: 190, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' } }}>
                        {months.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 13 }}>{m.label}{m.isCurrent ? '  (current)' : ''}</MenuItem>)}
                    </Select>
                </Stack>
            </Box>
            )}

            <Box sx={{ ...card, p: 2.5, mb: 2.5 }}>
                <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Payroll Cycle — {cycle?.payoutMonth || monthLabel}</Typography>
                        {cycle?.financialYear && <Chip icon={<CalendarMonthRoundedIcon sx={{ fontSize: '13px !important' }} />} label={`FY ${cycle.financialYear}`} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#E4F0F9', color: '#135F93', '& .MuiChip-icon': { color: '#135F93' } }} />}
                        {cycle?.attendanceMonth && <Chip label={`Attendance: ${cycle.attendanceMonth}`} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />}
                        {cycle?.status && <Chip label={prettyStatus(cycle.status)} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY }} />}
                    </Stack>
                    {renderAction()}
                </Stack>

                {loading ? (
                    <SkeletonRows count={5} />
                ) : !cycle ? (
                    <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', textAlign: 'center', py: 3 }}>
                        No payroll cycle found for {monthLabel}.
                    </Typography>
                ) : (
                    <PayrollCycleStepper
                        stages={STEPPER_STAGES}
                        current={activeIdx}
                        meta={STAGES.reduce((acc, st) => {
                            const s = stageState(st.key);
                            if (s.on || s.by) acc[st.key] = { on: s.on, by: s.by };
                            return acc;
                        }, {})}
                    />
                )}
            </Box>

            <Grid container spacing={2}>
                {totals.map((t) => {
                    const has = t.value !== null && t.value !== undefined;
                    return (
                        <Grid size={{ xs: 12, sm: 6, lg: 3 }} key={t.label}>
                            <Box sx={{ ...card, p: 2.5, bgcolor: t.bg, border: `1px solid ${t.color}22` }}>
                                <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: t.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</Typography>
                                        <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0F172A', mt: 0.5 }} noWrap>{has ? t.fmt(t.value) : '—'}</Typography>
                                        <Typography sx={{ fontSize: 10.5, color: '#6B7280', fontWeight: 600, mt: 0.3 }} noWrap>{has ? 'this cycle' : 'Pending calculation'}</Typography>
                                    </Box>
                                    <Box sx={{ width: 36, height: 36, borderRadius: '7px', bgcolor: '#fff', boxShadow: '0 1px 3px rgba(16,24,40,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ml: 1 }}>
                                        <t.icon sx={{ color: t.color, fontSize: 22 }} />
                                    </Box>
                                </Stack>
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}
